import React, { useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Column, Query, QueryResult } from 'material-table';
import { IOverrideEntityActions } from '../../API/Interfaces/IEntityActions';
import ISearchBaseDto from '../../API/Interfaces/ISearchBaseDto';
import EntityPage from '../../Layout/EntityPage';
import FileServiceAPI from '../../API/Services/FileServiceAPI';
import { IconButton, Tooltip } from '@material-ui/core';
import { DeleteIcon } from '../../Layout/Components/Icons';
import Modal from '../../Layout/Components/Modal';
import GraphicForm from './GraphicForm';
import { onSubmitProps } from '../../Constants/constants';
import { DictImageType } from '../../API/Interfaces/Enums';
import ConfirmDeleteModal, { IConfirmDeleteModal } from '../../Layout/Components/ConfirmDeleteModal';
import { IDimensions } from '../../Layout/Components/ModalFileUpload';
import NoRedirectingLink from '../../Layout/Components/NoRedirectingLink';
import { IImageDetailsDto } from '../../API/Interfaces/IFileDtos';

export interface IConfirmPopup {
	id: number | undefined;
	isVisible: boolean;
}

export interface IOpenPopup {
	open: boolean;
	edit: boolean;
}

interface IGraphicsTableProps {
	title: string;
	imageType: DictImageType;
	imageDimensions: IDimensions;
	hasEditRights?: boolean;
}

const MainPictures = (props: IGraphicsTableProps) => {
	const { enqueueSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const { title, imageType, imageDimensions, hasEditRights } = props;
	const tableRef = useRef();
	const [open, setOpen] = useState<IOpenPopup>({ open: false, edit: false });
	const [entity, setEntity] = useState<IImageDetailsDto>();
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<IConfirmPopup>();
	const errorDialog = (message?: string) =>
		enqueueSnackbar(`${t('common.errorOccured')}${message ? ': ' : ''}${message && message}`, { variant: 'error' });

	const fetchPictureData = async function (id: number) {
		try {
			const response = await FileServiceAPI.GetFileUrl(id);
			if (response) return response.data;
			else {
				errorDialog();
			}
		} catch (e) {
			errorDialog(e.message);
		}
		return undefined;
	};

	const headers: Array<Column<IImageDetailsDto>> = [
		{
			title: t('locations.table.corporationHeader'),
			field: 'corporationName',
			defaultSort: 'asc',
		},
		{
			title: title,
			field: 'name',
			render: (rowData) => (
				<NoRedirectingLink
					onClick={async () => {
						const response = await fetchPictureData(rowData.id);
						if (response) {
							setEntity({ ...response, imageId: rowData.id });
							setOpen({ open: true, edit: true });
						}
					}}
				>
					{rowData.name}
				</NoRedirectingLink>
			),
		},
		{
			headerStyle: { textAlign: 'right' },
			sorting: false,
			render: (rowData) =>
				!!props.hasEditRights && (
					<Tooltip title={t('table.body.deleteTooltip') || ''} style={{ marginLeft: 'auto', display: 'block' }}>
						<IconButton onClick={() => setIsDeleteModalOpen({ isVisible: true, id: rowData.id })}>
							<DeleteIcon isSelected={true} />
						</IconButton>
					</Tooltip>
				),
		},
	];

	const EntityActions: IOverrideEntityActions<IImageDetailsDto, IImageDetailsDto> = {
		api: FileServiceAPI,
		presetDataCallback: (data: any) => {
			return data;
		},
		setModalOpen: (e) => setOpen({ open: e, edit: false }),
		getTableData: async function (query: Query<IImageDetailsDto>) {
			const result: QueryResult<IImageDetailsDto> = {
				data: [],
				page: 0,
				totalCount: 0,
			};
			const request: ISearchBaseDto = {
				itemsPerPage: query.pageSize,
				pageNumber: query.page ? query.page + 1 : 1,
				searchPhrase: query.search,
				sortColumn: '',
				sortDirection: '',
			};

			if (query.orderBy) request.sortColumn = query.orderBy.field as string;

			if (query.orderDirection) request.sortDirection = query.orderDirection;

			try {
				const response = await FileServiceAPI.GetImages({ imageType: imageType }, request);

				if (response.success) {
					result.data = response.data.items;
					result.page = response.data.currentPage - 1;
					result.totalCount = response.data.totalItemsCount;

					return result;
				} else {
					let message = t(`errorCodes.${response.errors[0].errorCode}`);
					enqueueSnackbar(`${t('common.errorOccured')}: ${message}`, { variant: 'error' });
				}
			} catch (e) {
				errorDialog(e.message);
			}
			return result;
		},
	};

	const tableProps: any = {
		columns: headers,
		options: { paging: true },
	};

	let entityPageProps = {
		tableRef: tableRef,
		EntityActions: EntityActions,
		headers: headers,
		pageTitle: title,
		tableProps: tableProps,
		disableAddNew: !!!props.hasEditRights,
	};

	function refreshTable() {
		tableRef.current && (tableRef.current! as any).onQueryChange();
	}

	const callbacks: onSubmitProps = {
		presetDataCallback: EntityActions.presetDataCallback,
		setModalOpen: (e) => setOpen({ open: e, edit: false }),
		api: FileServiceAPI,
		refreshTable: refreshTable,
	};

	const handleDelete = async function () {
		try {
			const response = await FileServiceAPI.Delete(isDeleteModalOpen?.id!);
			if (response.success === true || response === 1) {
				setIsDeleteModalOpen({ isVisible: false, id: undefined });
				enqueueSnackbar(t('common.dataSaved'), { variant: 'success' });
				refreshTable();
			} else {
				enqueueSnackbar(t('common.errorOccured'), { variant: 'error' });
			}
		} catch (e) {
			errorDialog(e.message);
		}
	};

	const deleteModalProps: IConfirmDeleteModal = {
		handleDelete: handleDelete,
		isOpen: isDeleteModalOpen?.isVisible || false,
		handleClose: () => setIsDeleteModalOpen({ isVisible: false, id: undefined }),
	};

	return (
		<>
			<EntityPage {...entityPageProps} />
			{open.open && (
				<Modal
					form={<GraphicForm disabled={!hasEditRights} />}
					isOpen={open.open}
					callbacks={callbacks}
					entity={open.edit && entity}
					modalTitle={title}
					withFileUpload
					imageType={imageType}
					closeAfterUploadImage
					imageDimensions={imageDimensions}
					isReadOnly={!hasEditRights}
					onlyCancelButton={!hasEditRights}
				/>
			)}
			{isDeleteModalOpen?.isVisible && <ConfirmDeleteModal {...deleteModalProps} />}
		</>
	);
};

export default MainPictures;
